// Going to another webtop without reloading the page: goTo(href) fetches that webtop's record and swaps it in (the
// address through history.pushState), and Back / Forward do the same from popstate. The page, its modules, the
// widget stylesheets and this browser's icon cache all stay, so only the tiles are redrawn. Anything that is not a
// webtop of this server (another origin, a path that is no slug) still loads as a page.
// Requests still out for the webtop that was left answer with a `stale` error (api() in ui.js), so nothing they
// were going to do next lands on the new one.
import { stylesReady } from '../widgets/index.js';
import { arrange, cancelDrag, flushNudge } from './input.js';
import { appletService, LAUNCHER } from './applets.js';
import { renderLocked, showUnlock } from './lock.js';
import { hideMenu } from './menu.js';
import { addressSlug, renderHome, renderNotch, showTrash } from './notch.js';
import { closeOverlay } from './overlay.js';
import { render } from './render.js';
import { $webtop, selected, setWebtop, setSlug, slug } from './state.js';
import { api, toast } from './ui.js';
import { slugOfPath } from '/model.js';

// Each switch is numbered: a later one (or Back / Forward, or a link to the open webtop) makes an earlier one that
// is still loading give up, so only the last one is shown and the address always matches the webtop.
let switching = 0;

// Opens the webtop at `href` (`/@work`, `/`, `/@work?q=text`, `/@work#settings`). The current webtop stays on screen until the next one
// has arrived; a locked one shows its lock screen and the unlock dialog, as on a page load. `force` loads the
// current webtop again (after deleting it). `push: false` is for popstate, where the address has already changed;
// `scroll` is where that history entry was left. `home` says the `/` given is this browser's Home (the Home button).
// Every navigation closes the open overlay first, wherever it starts (a tile, the notch, a launcher entry, the
// assistant) and wherever it goes (the open webtop too), before anything is awaited, so it never waits on the record
// or stays open when that fails. Closing an overlay takes `q` and the hash off the address, so the comparison below
// sees the address without them.
export async function goTo(href, { force = false, push = true, scroll = 0, home = false } = {}) {
  closeOverlay();
  const url = new URL(href, location.href);
  // A link to `/` means root; an address already in the history (popstate) means what it does in this browser.
  const to = push && !home ? slugOfPath(url.pathname) : addressSlug(url.pathname);
  if (url.origin !== location.origin || !to) { location.href = url.href; return; }
  // With another webtop as Home, `/` is that webtop's (addressSlug), so root is shown at an address of its own.
  const path = to === 'root' && addressSlug('/') !== 'root' ? '/@root' : url.pathname;
  const n = ++switching;
  if (to === slug && !force) {
    if (push && path + url.search + url.hash !== location.pathname + location.search + location.hash) {
      history.pushState(null, '', path + url.search + url.hash);
    }
    launcherFromAddress();
    return;
  }
  let record = null;
  let how = null; // a locked webtop's {password, owned}, for the unlock dialog
  try {
    [record] = await Promise.all([api('GET', `/api/webtops/${to}`), stylesReady]);
  } catch (e) {
    if (n !== switching) return;
    // Not locked: the server could not answer. A popstate already shows the new address, so that page is loaded
    // in full (with its own error if the server is down); a click leaves this webtop as it was.
    if (!e.data?.locked) { if (push) toast(e); else location.reload(); return; }
    how = e.data;
  }
  if (n !== switching) return;
  leave();
  setSlug(to);
  if (push) {
    history.replaceState({ ...history.state, scroll: scrollY }, ''); // for Back to come back to
    history.pushState(null, '', path + url.search + url.hash);
  } else if (url.search + url.hash !== location.search + location.hash) {
    // Closing an overlay in leave() took the hash or `q` off this entry; it is what this webtop is opened with.
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  }
  scrollTo(0, 0);
  if (record) {
    setWebtop(record);
    render();
    if (scroll) scrollTo(0, scroll);
  } else {
    renderLocked();
    showUnlock(how);
  }
  renderHome(); // render() leaves the Home button alone when neither webtop is Home, and a lock screen never renders
  renderNotch();
  launcherFromAddress();
}

// A ?q=text or #section in the address the page moved to opens the launcher with it (the Launcher applet's).
const launcherFromAddress = () => appletService(LAUNCHER).then((l) => l.fromAddress()).catch(toast);

// What belongs to the webtop being left, ended while it is still the open one: the field being typed in (blurring
// a note saves what was typed since its last save), arrow-key moves not saved yet, a drag, the menu, the overlay
// (the group panel, the launcher...), open dialogs and the selection. Its tiles go with the next render() (an
// embedded player too: only this webtop's tiles are kept), and so does an error shown instead of them. A dialog's
// `close` event comes later, once the next webtop is drawn, so a close handler must not count on the webtop it was
// opened on (the dialogs' current ones only settle their promise or check `webtop`).
function leave() {
  document.activeElement?.blur();
  flushNudge();
  cancelDrag();
  arrange(false);
  showTrash(false);
  hideMenu();
  closeOverlay();
  document.querySelectorAll('dialog[open]').forEach((d) => d.close());
  selected.clear();
  [...$webtop.childNodes].filter((c) => c.nodeType === Node.TEXT_NODE).forEach((c) => c.remove());
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  // The page restores the scroll itself (goTo's `scroll`): the browser's own restoring would scroll the old tiles
  // to the new entry's spot before the next webtop has arrived.
  history.scrollRestoration = 'manual';
  // Back and Forward between webtops, and within this one between what the launcher shows; a hash typed or followed
  // fires this too. Heard here and not on hashchange, which Chrome skips when `q` changes as well. An address that says
  // what to open swaps it in place, so nothing blinks; one saying nothing closes what is open. Either way a switch on
  // its way is cancelled (the address is this webtop's again).
  addEventListener('popstate', (e) => {
    const to = addressSlug(location.pathname);
    if (to !== slug) { goTo(location.href, { push: false, scroll: e.state?.scroll ?? 0 }); return; }
    switching++;
    if (location.hash || new URLSearchParams(location.search).has('q')) launcherFromAddress();
    else closeOverlay();
  });
}
